export const DASHBOARD_PATH = '/' as const;

export const SERVERS_HUB_PATHS = {
  list: '/servers',
  runtime: '/servers/runtime',
  services: '/servers/services',
  sites: '/servers/sites',
  domainCertificates: '/servers/domain-certificates',
  timeline: '/servers/timeline',
} as const;

export const PROJECTS_HUB_PATHS = {
  list: '/projects',
  repos: '/projects/repos',
  releases: '/projects/releases',
  runs: '/projects/runs',
  /** @deprecated use releases */
  deployments: '/projects/releases',
  /** @deprecated use releases */
  releasePlans: '/projects/releases',
  /** @deprecated use runs */
  deploymentRuns: '/projects/runs',
  /** @deprecated use runs */
  releaseRuns: '/projects/runs',
} as const;

export const SERVER_FORM_PATHS = {
  create: '/servers/new',
  edit: (id: string) => `/servers/${id}/edit`,
} as const;

export const SITE_FORM_PATHS = {
  create: '/servers/sites/new',
  edit: (id: string) => `/servers/sites/${id}/edit`,
} as const;

export const PROJECT_FORM_PATHS = {
  create: '/projects/new',
  edit: (id: string) => `/projects/${id}/edit`,
  environmentCreate: (projectId: string) => `/projects/${projectId}/environments/new`,
  releasePlanCreate: (projectId: string) => `/projects/${projectId}/release-plans/new`,
} as const;

export const SETTINGS_PATHS = {
  general: '/settings',
  /** @deprecated bookmark redirect only — SSH keys live in Credential Vault */
  sshKeys: '/settings/ssh-keys',
  teams: '/settings/teams',
  cloudflare: '/settings/cloudflare',
} as const;

/** Credential Vault UI (kind=ssh_key etc.). */
export const VAULT_PATH = '/secrets?tab=vault' as const;

export const PUBLIC_STATUS_PATH = '/public-status' as const;

/** @deprecated use DASHBOARD_PATH */
export const DASHBOARD_PATHS = {
  repos: PROJECTS_HUB_PATHS.repos,
  runtime: SERVERS_HUB_PATHS.runtime,
  services: SERVERS_HUB_PATHS.services,
} as const;

export function isDashboardPath(pathname: string): boolean {
  return pathname === DASHBOARD_PATH;
}

export function isServersHubPath(pathname: string): boolean {
  return (
    pathname === SERVERS_HUB_PATHS.list ||
    pathname === SERVERS_HUB_PATHS.runtime ||
    pathname === SERVERS_HUB_PATHS.services ||
    pathname === SERVERS_HUB_PATHS.sites ||
    pathname === SERVERS_HUB_PATHS.domainCertificates ||
    pathname === SERVERS_HUB_PATHS.timeline
  );
}

export function isServersNavActive(pathname: string): boolean {
  return pathname === '/servers' || pathname.startsWith('/servers/');
}

/**
 * Servers inventory + server detail paths.
 * Matches `/servers` and `/servers/:id…` but NOT Ops leaf hubs (runtime/services/sites/certs).
 */
export function isOpsServersInventoryPath(pathname: string): boolean {
  if (pathname === SERVERS_HUB_PATHS.list || pathname === `${SERVERS_HUB_PATHS.list}/`) return true;
  if (!pathname.startsWith(`${SERVERS_HUB_PATHS.list}/`)) return false;
  if (
    isOpsRuntimePath(pathname) ||
    isOpsServicesPath(pathname) ||
    isOpsSitesPath(pathname) ||
    isOpsDomainCertsPath(pathname) ||
    isOpsTimelinePath(pathname)
  ) {
    return false;
  }
  return true;
}

export function isOpsTimelinePath(pathname: string): boolean {
  return (
    pathname === SERVERS_HUB_PATHS.timeline ||
    pathname.startsWith(`${SERVERS_HUB_PATHS.timeline}/`)
  );
}

export function isOpsRuntimePath(pathname: string): boolean {
  return pathname === SERVERS_HUB_PATHS.runtime || pathname.startsWith(`${SERVERS_HUB_PATHS.runtime}/`);
}

export function isOpsServicesPath(pathname: string): boolean {
  return pathname === SERVERS_HUB_PATHS.services || pathname.startsWith(`${SERVERS_HUB_PATHS.services}/`);
}

export function isOpsSitesPath(pathname: string): boolean {
  return pathname === SERVERS_HUB_PATHS.sites || pathname.startsWith(`${SERVERS_HUB_PATHS.sites}/`);
}

export function isOpsDomainCertsPath(pathname: string): boolean {
  return (
    pathname === SERVERS_HUB_PATHS.domainCertificates ||
    pathname.startsWith(`${SERVERS_HUB_PATHS.domainCertificates}/`)
  );
}

export function serversHubTab(pathname: string): string {
  if (pathname === SERVERS_HUB_PATHS.runtime) return 'runtime';
  if (pathname === SERVERS_HUB_PATHS.services) return 'services';
  if (pathname === SERVERS_HUB_PATHS.sites) return 'sites';
  if (pathname === SERVERS_HUB_PATHS.domainCertificates) return 'domain-certificates';
  return 'inventory';
}

export function isProjectsHubPath(pathname: string): boolean {
  return (
    pathname === PROJECTS_HUB_PATHS.list ||
    pathname.startsWith(`${PROJECTS_HUB_PATHS.repos}`) ||
    pathname.startsWith(`${PROJECTS_HUB_PATHS.releases}`) ||
    pathname.startsWith(`${PROJECTS_HUB_PATHS.runs}`) ||
    pathname.startsWith('/projects/deployments') ||
    pathname.startsWith('/projects/release-runs')
  );
}

export function isProjectsNavActive(pathname: string): boolean {
  return (
    pathname.startsWith('/projects') ||
    pathname.startsWith('/deployments') ||
    pathname.startsWith('/pipeline-runs')
  );
}

/**
 * Projects inventory root + project detail trees.
 * Matches `/projects` and `/projects/:id…` but NOT repos/releases/runs hub leaves
 * (or their legacy deployment aliases).
 */
export function isProjectsListPath(pathname: string): boolean {
  if (pathname === PROJECTS_HUB_PATHS.list || pathname === `${PROJECTS_HUB_PATHS.list}/`) return true;
  if (!pathname.startsWith(`${PROJECTS_HUB_PATHS.list}/`)) return false;
  if (
    isProjectsReposPath(pathname) ||
    isProjectsReleasesPath(pathname) ||
    isProjectsRunsPath(pathname)
  ) {
    return false;
  }
  return true;
}

export function isProjectsReposPath(pathname: string): boolean {
  return (
    pathname === PROJECTS_HUB_PATHS.repos ||
    pathname.startsWith(`${PROJECTS_HUB_PATHS.repos}/`) ||
    pathname === '/workflows' ||
    pathname.startsWith('/workflows/')
  );
}

export function isProjectsReleasesPath(pathname: string): boolean {
  // Legacy `/projects/deployments/runs` belongs to runs, not releases.
  if (
    pathname.startsWith(`${PROJECTS_HUB_PATHS.runs}`) ||
    pathname.startsWith('/projects/release-runs') ||
    pathname.startsWith('/projects/deployments/runs')
  ) {
    return false;
  }
  return (
    pathname === PROJECTS_HUB_PATHS.releases ||
    pathname.startsWith(`${PROJECTS_HUB_PATHS.releases}/`) ||
    pathname.startsWith('/projects/deployments')
  );
}

export function isProjectsRunsPath(pathname: string): boolean {
  if (
    pathname === PROJECTS_HUB_PATHS.runs ||
    pathname.startsWith(`${PROJECTS_HUB_PATHS.runs}/`) ||
    pathname.startsWith('/projects/release-runs') ||
    pathname.startsWith('/projects/deployments/runs') ||
    pathname === '/deployments/runs' ||
    pathname.startsWith('/deployments/runs/') ||
    pathname === '/pipeline-runs' ||
    pathname.startsWith('/pipeline-runs/')
  ) {
    return true;
  }
  // `/deployments/:id` redirects onto a run. Release aliases stay on the releases item.
  if (!pathname.startsWith('/deployments/')) return false;
  const rest = pathname.slice('/deployments/'.length);
  return rest.length > 0 && rest !== 'pipelines' && !rest.startsWith('pipelines/');
}

export function projectsHubTab(pathname: string): string {
  if (pathname.startsWith(`${PROJECTS_HUB_PATHS.repos}`)) return 'repos';
  if (
    pathname.startsWith(`${PROJECTS_HUB_PATHS.runs}`) ||
    pathname.startsWith('/projects/release-runs') ||
    pathname.startsWith('/projects/deployments/runs')
  ) {
    return 'runs';
  }
  if (
    pathname.startsWith(`${PROJECTS_HUB_PATHS.releases}`) ||
    pathname.startsWith('/projects/deployments')
  ) {
    return 'releases';
  }
  return 'projects';
}

export function isSettingsHubPath(pathname: string): boolean {
  return pathname === SETTINGS_PATHS.general || pathname.startsWith(`${SETTINGS_PATHS.general}/`);
}

export function isSettingsNavActive(pathname: string): boolean {
  return (
    isSettingsHubPath(pathname) ||
    pathname === '/ssh-keys' ||
    pathname.startsWith('/ssh-keys/') ||
    pathname === '/teams' ||
    pathname.startsWith('/teams/') ||
    pathname === '/cloudflare' ||
    pathname.startsWith('/cloudflare/')
  );
}

/** Settings general root only — not teams/cloudflare (or legacy ssh-keys redirect). */
export function isSettingsGeneralPath(pathname: string): boolean {
  return pathname === SETTINGS_PATHS.general || pathname === `${SETTINGS_PATHS.general}/`;
}

/** @deprecated SSH keys UI removed; path only used for bookmark redirect. */
export function isSettingsSshKeysPath(pathname: string): boolean {
  return (
    pathname === SETTINGS_PATHS.sshKeys ||
    pathname.startsWith(`${SETTINGS_PATHS.sshKeys}/`) ||
    pathname === '/ssh-keys' ||
    pathname.startsWith('/ssh-keys/')
  );
}

export function isSettingsTeamsPath(pathname: string): boolean {
  return (
    pathname === SETTINGS_PATHS.teams ||
    pathname.startsWith(`${SETTINGS_PATHS.teams}/`) ||
    pathname === '/teams' ||
    pathname.startsWith('/teams/')
  );
}

export function isSettingsCloudflarePath(pathname: string): boolean {
  return (
    pathname === SETTINGS_PATHS.cloudflare ||
    pathname.startsWith(`${SETTINGS_PATHS.cloudflare}/`) ||
    pathname === '/cloudflare' ||
    pathname.startsWith('/cloudflare/')
  );
}

export function settingsHubTab(pathname: string): string {
  // Legacy /settings/ssh-keys redirects to vault; treat as general if still mounted briefly.
  if (pathname.startsWith(SETTINGS_PATHS.teams)) return 'teams';
  if (pathname.startsWith(SETTINGS_PATHS.cloudflare)) return 'cloudflare';
  return 'general';
}
